import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import { deepClonePoints } from './anchor-points'
import { shiftSubpathsAfterRemove, subpathIndexAt } from './subpath-ranges'

/** What the shape holds — a photo or text container keeps at least 3 nodes per subpath. Each
 *  caller decides what counts as a container (the two pen-edit removal paths differ). */
interface AnchorContainerKind {
  isImageContainer: boolean
  isTextContainer: boolean
}

export type RemoveAnchorResult =
  /** Refused because the shape is a container that would drop below 3 nodes — warn the user. */
  | { kind: 'blocked'; message: string }
  /** Too few points to remove one; nothing changes. */
  | { kind: 'unchanged' }
  | { kind: 'removed'; points: PenAnchorPoint[]; subpaths: PenSubpathRange[]; isClosed: boolean }

/**
 * Remove the anchor at `index` (guard: need at least 2 points for open, 3 for closed) — scoped to
 * the subpath containing it, not the whole (possibly compound) points array.
 * Exception: a 3-node closed subpath can still lose a node — the result becomes
 * an open 2-node line rather than staying closed.
 */
export function removeAnchor(
  pts: PenAnchorPoint[],
  subpaths: PenSubpathRange[],
  index: number,
  { isImageContainer, isTextContainer }: AnchorContainerKind
): RemoveAnchorResult {
  const subpathIdx = subpathIndexAt(subpaths, index)
  const sp = subpaths[subpathIdx] ?? { start: 0, end: pts.length, isClosed: false }
  const nLocal = sp.end - sp.start

  if ((isImageContainer || isTextContainer) && nLocal <= 3) {
    return {
      kind: 'blocked',
      message: isImageContainer
        ? 'Cannot remove node — image containers must have at least 3 nodes.'
        : 'Cannot remove node — text containers must have at least 3 nodes.',
    }
  }

  const convertToOpenLine = sp.isClosed && nLocal === 3
  const minPoints = sp.isClosed ? 3 : 2

  if (!convertToOpenLine && nLocal <= minPoints) return { kind: 'unchanged' }

  const relIdx = index - sp.start
  const updated = deepClonePoints(pts)

  updated.splice(index, 1)

  // Clear handles on the neighboring nodes that were pointing toward the removed node,
  // so the path segment between them uses clean geometry rather than curving through
  // the area the removed node occupied.
  let prevIdx = -1
  let nextIdx = -1

  if (relIdx > 0) {
    prevIdx = index - 1
  } else if (sp.isClosed) {
    prevIdx = sp.end - 2
  }

  if (relIdx < nLocal - 1) {
    nextIdx = index
  } else if (sp.isClosed) {
    nextIdx = sp.start
  }

  if (prevIdx >= 0) {
    updated[prevIdx] = { ...updated[prevIdx], handleOut: null }
  }

  if (nextIdx >= 0) {
    updated[nextIdx] = { ...updated[nextIdx], handleIn: null }
  }

  const updatedSubpaths = shiftSubpathsAfterRemove(subpaths, subpathIdx, index, convertToOpenLine)

  return {
    kind: 'removed',
    points: updated,
    subpaths: updatedSubpaths,
    isClosed: updatedSubpaths[0]?.isClosed ?? false,
  }
}
